import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Calculator, BarChart3, ArrowRight, Building2, ShoppingCart, Users,
  History, Calendar, MessageSquare, GitCompareArrows, GanttChart,
  Mail, LineChart, CheckCircle2, Target
} from 'lucide-react';

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const capabilities = [
    {
      icon: Calculator,
      title: 'Estimate',
      subtitle: 'Effort, Cost & Complexity',
      description: 'Configure your LMS scope and get detailed effort, cost, and risk analysis in minutes — not weeks.',
      accent: '#4ABFCF',
    },
    {
      icon: GitCompareArrows,
      title: 'Compare',
      subtitle: 'Scenario Analysis',
      description: 'Run side-by-side comparisons of different implementation approaches to find your optimal path.',
      accent: '#6366F1',
    },
    {
      icon: GanttChart,
      title: 'Plan',
      subtitle: 'Timeline & Resources',
      description: 'Model project timelines, allocate resources across phases, and run what-if scenarios to stress-test your plan.',
      accent: '#F59E0B',
    },
    {
      icon: Mail,
      title: 'Share & Approve',
      subtitle: 'Stakeholder Buy-in',
      description: 'Export branded reports in any format and email snapshots directly to stakeholders for fast approval.',
      accent: '#10B981',
    },
    {
      icon: LineChart,
      title: 'Insights',
      subtitle: 'Analytics & Intelligence',
      description: 'Cross-estimate analytics, feasibility scoring, risk flags, and data-driven recommendations across all your projects.',
      accent: '#EC4899',
    },
  ];

  const steps = [
    { step: 1, title: 'Scope Your LMS', description: 'Select implementation type, components, and complexity to generate your estimate.' },
    { step: 2, title: 'Compare & Optimize', description: 'Run multiple scenarios side-by-side and identify the best-fit approach.' },
    { step: 3, title: 'Model the Plan', description: 'Generate timelines, allocate resources, and stress-test with what-if analysis.' },
    { step: 4, title: 'Share & Get Approval', description: 'Export polished reports and email them to stakeholders — ready for sign-off.' },
  ];

  const implementationTypes = [
    { icon: Building2, title: 'Corporate LMS', description: 'Internal employee training and development' },
    { icon: ShoppingCart, title: 'Extended Enterprise', description: 'External partners, customers, and ecommerce' },
    { icon: Users, title: 'Workforce Enablement', description: 'Compliance, safety, and operational training' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/seertech-mark.png"
                alt="Seertech"
                className="h-11 w-11 object-contain shrink-0"
                data-testid="seertech-logo"
              />
              <div className="hidden sm:block">
                <div className="text-xl font-bold text-[#4ABFCF] tracking-tight font-['Outfit']">Blueprint Studio</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">by Seertech</div>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              {user ? (
                <>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/history')} className="text-slate-600 hover:text-[#0F2040]" data-testid="nav-history">
                    <History className="w-4 h-4 mr-1.5" /> History
                  </Button>
                  <Button onClick={() => navigate('/estimator/new')} className="bg-[#4ABFCF] hover:bg-[#3AAAB9] text-white rounded-xl" size="sm" data-testid="nav-new-estimate">
                    <Calculator className="w-4 h-4 mr-1.5" /> New Estimate
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/login')} className="text-slate-600 hover:text-[#0F2040]" data-testid="nav-login">
                    Sign In
                  </Button>
                  <Button onClick={() => navigate('/login')} className="bg-[#4ABFCF] hover:bg-[#3AAAB9] text-white rounded-xl" size="sm" data-testid="nav-register">
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="text-center py-16 md:py-24 animate-fadeInUp">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4ABFCF]/10 text-[#0F2040] text-sm font-medium mb-8" data-testid="hero-badge">
            <Target className="w-4 h-4 text-[#4ABFCF]" />
            Estimate &middot; Compare &middot; Plan &middot; Approve
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F2040] tracking-tight mb-6 font-['Outfit']">
            From Estimate to Approval
            <span className="block text-[#4ABFCF]">Plan with Confidence</span>
          </h1>

          <p className="text-lg md:text-xl text-slate-500 max-w-3xl mx-auto mb-10 leading-relaxed">
            Scope effort and cost, compare scenarios, model timelines and resources, 
            then share polished reports with stakeholders — all in one platform.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate(user ? '/estimator' : '/login')}
              className="bg-[#4ABFCF] hover:bg-[#3AAAB9] text-white px-8 py-6 text-lg rounded-xl shadow-sm"
              data-testid="hero-cta-button"
            >
              Start Planning
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate(user ? '/history' : '/login')}
              className="border-[#0F2040]/20 text-[#0F2040] px-8 py-6 text-lg rounded-xl hover:bg-[#0F2040]/5"
              data-testid="hero-signin-button"
            >
              <History className="w-5 h-5 mr-2" />
              View Past Estimates
            </Button>
          </div>
        </div>
      </section>

      {/* Platform Capabilities */}
      <section className="py-16" data-testid="capabilities-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F2040] font-['Outfit'] tracking-tight mb-4">
              One Platform, Five Powerful Capabilities
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Everything you need to go from initial scoping to stakeholder approval
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            {capabilities.map((cap, index) => (
              <Card 
                key={cap.title}
                className="border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-300 group cursor-pointer"
                onClick={() => navigate(user ? '/estimator/new' : '/login')}
                data-testid={`capability-card-${index}`}
              >
                <CardContent className="p-5">
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-3 transition-colors"
                    style={{ backgroundColor: `${cap.accent}15`, color: cap.accent }}>
                    <cap.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-[#0F2040] text-sm mb-0.5">{cap.title}</h3>
                  <p className="text-[11px] font-medium mb-2" style={{ color: cap.accent }}>{cap.subtitle}</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{cap.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 rounded-2xl border border-slate-200 p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#0F2040] font-['Outfit'] tracking-tight mb-4">
                  Stop Guessing.<br />Start Planning with Data.
                </h2>
                <p className="text-slate-500 leading-relaxed mb-6">
                  Replace spreadsheets and back-of-napkin estimates with a platform that gives you defensible numbers, 
                  realistic timelines, and the confidence to present your plan to leadership.
                </p>
              </div>
              <div className="space-y-4">
                {[
                  'Industry-validated estimation models',
                  'Compare scenarios before committing',
                  'Resource planning aligned with reality',
                  'Branded exports ready for sign-off',
                  'Cross-project analytics and risk intelligence',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#4ABFCF] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#0F2040] font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F2040] text-center mb-12 font-['Outfit'] tracking-tight">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((item) => (
              <div key={item.step} className="text-center" data-testid={`how-it-works-${item.step}`}>
                <div className="w-12 h-12 rounded-full bg-[#4ABFCF] text-white text-lg font-bold flex items-center justify-center mx-auto mb-4 shadow-sm">
                  {item.step}
                </div>
                <h3 className="font-semibold text-[#0F2040] mb-2 text-sm">{item.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Types */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F2040] font-['Outfit'] tracking-tight mb-4">
              Tailored for Your Use Case
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Specialized calculations for different implementation scenarios
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {implementationTypes.map((type, index) => (
              <Card 
                key={type.title}
                className="border border-slate-200 hover:border-[#4ABFCF]/40 hover:shadow-md transition-all duration-300 cursor-pointer group"
                onClick={() => navigate(user ? '/estimator/new' : '/login')}
                data-testid={`landing-impl-type-${index}`}
              >
                <CardContent className="p-8 text-center">
                  <div className="w-14 h-14 rounded-xl bg-[#4ABFCF]/10 text-[#4ABFCF] flex items-center justify-center mx-auto mb-5 group-hover:bg-[#4ABFCF]/20 transition-colors">
                    <type.icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0F2040] mb-2">{type.title}</h3>
                  <p className="text-sm text-slate-500">{type.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Seertech CTA Banner */}
      <section className="py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gradient-to-r from-[#0F2040] to-[#1a3a6e] text-white overflow-hidden shadow-lg" data-testid="seertech-cta-banner">
            <div className="p-8 md:p-12">
              <div className="max-w-3xl mx-auto text-center">
                <img 
                  src="https://www.seertechsolutions.com/wp-content/uploads/2025/08/Seertech-logo-white.svg" 
                  alt="Seertech Solutions" className="h-8 mx-auto mb-6 opacity-90" width="300" height="150"
                />
                <h3 className="text-2xl md:text-3xl font-bold mb-4 font-['Outfit']">
                  Let Seertech Help You Plan Your Implementation
                </h3>
                <p className="text-slate-300 mb-8 text-lg leading-relaxed">
                  Discover how Seertech's Learning Enablement Platform can streamline your implementation and deliver measurable impact.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="https://www.seertechsolutions.com/demo-ai-powered-lms/" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-[#4ABFCF] hover:bg-[#3AAAB9] text-white px-8 py-3 text-base rounded-xl font-medium transition-colors shadow-sm"
                    data-testid="cta-book-demo">
                    <Calendar className="w-5 h-5" /> Book a Demo
                  </a>
                  <a href="https://www.seertechsolutions.com/contact/" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 border border-white/30 bg-white/10 hover:bg-white/20 text-white px-8 py-3 text-base rounded-xl font-medium transition-colors"
                    data-testid="cta-contact">
                    <MessageSquare className="w-5 h-5" /> Contact Us <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dark Footer */}
      <footer className="mt-12 bg-[#0B1628]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <img 
                src="https://www.seertechsolutions.com/wp-content/uploads/2025/08/Seertech-logo-white.svg" 
                alt="Seertech Logo" className="h-7 w-auto mb-4 opacity-80"
              />
              <p className="text-sm text-slate-400 leading-relaxed">
                Seertech Blueprint Studio helps organizations estimate, compare, plan, and approve their learning platform deployments with confidence.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4 text-sm">Resources</h4>
              <ul className="space-y-2.5">
                <li><a href="https://www.seertechsolutions.com/resources/" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-[#4ABFCF] transition-colors">Learning Resources</a></li>
                <li><a href="https://www.seertechsolutions.com/solutions/" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-[#4ABFCF] transition-colors">Solutions</a></li>
                <li><a href="https://www.seertechsolutions.com/demo-ai-powered-lms/" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-[#4ABFCF] transition-colors">Book a Demo</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4 text-sm">Contact</h4>
              <ul className="space-y-2.5">
                <li><a href="mailto:info@seertechsolutions.com" className="text-sm text-slate-400 hover:text-[#4ABFCF] transition-colors">info@seertechsolutions.com</a></li>
                <li><a href="https://www.seertechsolutions.com" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-[#4ABFCF] transition-colors">seertechsolutions.com</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-500">&copy; 2026 Seertech Solutions. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">Privacy Policy</a>
              <a href="#" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
